import React, { useEffect } from 'react';
import { OFFICIAL_LINKS } from '../data/servicesData';
import { X, CheckCircle2, ExternalLink, Zap, Sparkles, MessageCircle, Send } from 'lucide-react';

export const ProductDetailsModal = ({
  product,
  onClose,
  onOpenInquiry,
}) => {
  useEffect(() => {
    if (!product) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [product, onClose]);

  if (!product) return null;

  const isGreen = product.colorTheme === 'green' || product.isInsurance;

  const openWhatsAppForProduct = () => {
    const text = encodeURIComponent(
      `Hello Fi Pay Bills Team,\nI am interested in "${product.title}" (${product.shortTag}). Please share onboarding steps, API specifications, and commercial rates.`
    );
    window.open(`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${text}`, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="fi-pop relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden my-6"
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className={`p-6 sm:p-7 text-white flex items-start justify-between ${
          isGreen ? 'bg-gradient-to-r from-emerald-800 to-teal-900' : 'bg-gradient-to-r from-[#0c2e6b] to-[#124294]'
        }`}>
          <div className="space-y-2 pr-4">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white/15 text-white border border-white/20">
                {product.shortTag}
              </span>
              {product.settlementTime && (
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-300" />
                  <span>{product.settlementTime}</span>
                </span>
              )}
            </div>

            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white">
              {product.title}
            </h3>

            <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
              {product.tagline || product.description}
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 sm:p-7 space-y-6 max-h-[72vh] overflow-y-auto">
          {/* WHAT WE ARE PROVIDING SECTION */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isGreen ? 'bg-emerald-600' : 'bg-blue-600'}`} />
              <h4 className="text-xs sm:text-sm font-black uppercase tracking-wider text-slate-900">
                What We Are Providing
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-2.5">
              {product.whatWeProvide.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200/80 hover:border-slate-300 transition-colors"
                >
                  <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${isGreen ? 'text-emerald-600' : 'text-blue-600'}`} />
                  <span className="text-xs sm:text-sm text-slate-700 leading-relaxed font-medium">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Supported Aggregators & Partners */}
          {product.partnerIntegrations && product.partnerIntegrations.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                Integrated Aggregators &amp; Partners
              </span>
              <div className="flex flex-wrap gap-2">
                {product.partnerIntegrations.map((p, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 rounded-lg text-xs font-semibold bg-blue-50/80 text-blue-900 border border-blue-100"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Commission / Refer & Earn Info */}
          {product.commissionInfo && (
            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 flex items-center gap-2.5">
              <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-xs font-bold">
                {product.commissionInfo}
              </span>
            </div>
          )}

          {/* Action Row with WhatsApp + Submit + Direct Link */}
          <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center gap-2.5">
            {/* Primary Submit Button */}
            <button
              onClick={() => {
                onClose();
                onOpenInquiry(product.title);
              }}
              className={`w-full sm:flex-1 py-3 px-4 rounded-xl text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md ${
                isGreen 
                  ? 'bg-emerald-700 hover:bg-emerald-800 shadow-emerald-700/20' 
                  : 'bg-blue-700 hover:bg-blue-800 shadow-blue-700/20'
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              <span>{product.directActionText || 'Submit Request'}</span>
            </button>

            {/* Direct WhatsApp Button */}
            <button
              onClick={openWhatsAppForProduct}
              className="w-full sm:w-auto py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
              title="Chat with Hyderabad Desk on WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </button>

            {product.referralLink && (
              <a
                href={product.referralLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto py-3 px-4 rounded-xl border border-slate-300 text-slate-800 hover:bg-slate-50 font-bold text-xs uppercase tracking-wider text-center transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <span>Direct Link</span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
              </a>
            )}

            <button
              onClick={onClose}
              className="w-full sm:w-auto py-3 px-3.5 rounded-xl text-slate-600 hover:bg-slate-100 text-xs font-semibold transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
